import { navigateTo, useLinkResolver, useRuntimeConfig } from '#imports'
import { type Ref, computed, ref } from 'vue'

import {
  type CountryCodeValue,
  MarketCountryCode,
} from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'

import { HOME } from '~/scopes/home/route-names'

export function useCountrySwitchRedirection(
  detectedCountryCode: Ref<string | undefined>,
) {
  const thisMarket = useMarketplace().market
  const { FF_ENABLE_ES_US_LOCALE } = useRuntimeConfig().public

  const { getLink } = useLinkResolver()
  const localStorage = useLocalStorage()
  const isBannerClosed = ref(false)

  const linkToDetectedMarket = computed(() => {
    if (!detectedCountryCode) {
      return null
    }

    return (
      getLink({
        link: { name: HOME },
        countryCode:
          MarketCountryCode[detectedCountryCode.value as CountryCodeValue],
      })?.href ?? ''
    )
  })

  const isRedirectionSuggested = computed(() => {
    const alreadyAsked =
      isBannerClosed.value || localStorage.hasKey('country_switch_clicked_at')

    // if the detected user country code is US, then we don't suggest
    // a redirection if the multi locale FF is active
    const hideMultiLocaleRedirect =
      FF_ENABLE_ES_US_LOCALE &&
      detectedCountryCode.value === MarketCountryCode.US

    return (
      !alreadyAsked &&
      detectedCountryCode &&
      detectedCountryCode.value !== thisMarket.countryCode &&
      !hideMultiLocaleRedirect
    )
  })

  const handleClose = () => {
    isBannerClosed.value = true
    localStorage.setItem('country_switch_clicked_at', Date.now().toString())
  }
  const handleRedirection = () => {
    handleClose()
    void navigateTo(linkToDetectedMarket.value, { external: true })
  }

  return {
    isRedirectionSuggested,
    currentMarketCountryCode: thisMarket.countryCode,
    linkToDetectedMarket,
    handleClose,
    handleRedirection,
  }
}
